import { useState, useEffect, useRef } from "react";
import { Palette, Check } from "lucide-react";
import themes from "../config/themes.json";
import "../styles/themeSelector.css";
import { API_BASE } from "../config";
import { getSession } from "../session";
import apiFetch from "../apiFetch";

const THEME_KEY = "rq_theme";

export function applyTheme(themeId) {
  const theme = themes.find((t) => t.id === themeId) || themes[0];
  const root = document.documentElement;
  Object.entries(theme.variables).forEach(([k, v]) => root.style.setProperty(k, v));
  localStorage.setItem(THEME_KEY, themeId);
}

export function loadTheme() {
  const saved = localStorage.getItem(THEME_KEY) || "midnight";
  applyTheme(saved);
  return saved;
}

export default function ThemeSelector() {
  const session = getSession();
  const userId = session?.userId;
  const [open, setOpen] = useState(false);
  const [activeId, setActiveId] = useState(localStorage.getItem(THEME_KEY) || "midnight");
  const panelRef = useRef(null);

  useEffect(() => {
    if (!userId) return;
    apiFetch(`/api/user/theme/${userId}`)
      .then((r) => r.json())
      .then((d) => {
        const id = d.themeId || "midnight";
        setActiveId(id);
        applyTheme(id);
      })
      .catch(() => {});
  }, [userId]);

  useEffect(() => {
    const handleClick = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  const selectTheme = (id) => {
    setActiveId(id);
    applyTheme(id);
    setOpen(false);
    if (userId) {
      apiFetch(`/api/user/theme`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId, themeId: id }),
      }).catch(() => {});
    }
  };

  return (
    <div className="ts-wrap" ref={panelRef}>
      <button
        className="ts-trigger"
        onClick={() => setOpen((p) => !p)}
        title="Change theme"
        aria-label="Change theme"
      >
        <Palette size={18} />
      </button>

      {open && (
        <div className="ts-panel">
          <div className="ts-panel-title">Choose a theme</div>
          <div className="ts-grid">
            {themes.map((t) => (
              <button
                key={t.id}
                className={`ts-swatch ${activeId === t.id ? "ts-swatch--active" : ""}`}
                onClick={() => selectTheme(t.id)}
                title={t.description}
              >
                <span
                  className="ts-preview"
                  style={{
                    background: `linear-gradient(135deg, ${t.preview[0]}, ${t.preview[1]})`,
                  }}
                >
                  {activeId === t.id && <Check size={14} className="ts-check" />}
                </span>
                <span className="ts-label">{t.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
